import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import type { Shop, ShopStage, ShopVisit } from '../api/types';
import { useApp } from '../state/AppContext';
import {
  EmptyRow, ErrorBanner, ExportMenu, MultiSelect, SortableHeader, downloadCsv, formatDate, today, useTableSort,
} from '../components/ui';
import { STAGE_LABEL, ShopForm, StageText, VisitForm, money, openEstimate, shopLabel } from '../components/ShopForms';

const STAGES: ShopStage[] = ['awaiting_estimate', 'awaiting_decision', 'approved', 'denied', 'picked_up'];

/**
 * Units at repair shops, and the shops themselves — Beth's Shop Locations tab.
 * Opens on what is still out: a unit is open until it is picked up, and the
 * ones waiting on an approval are the ones somebody has to act on.
 */
export function Shops() {
  const { can } = useApp();
  const [tab, setTab] = useState<'visits' | 'shops'>('visits');
  const canLog = can('vehicles', 'shops');

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Shops</div>
          <div className="page-subtitle">Units at repair shops, and the shops we use</div>
        </div>
      </div>

      <div className="segmented" style={{ marginBottom: 16 }}>
        <button className={`segmented__item${tab === 'visits' ? ' segmented__item--active' : ''}`} onClick={() => setTab('visits')}>
          Visits
        </button>
        <button className={`segmented__item${tab === 'shops' ? ' segmented__item--active' : ''}`} onClick={() => setTab('shops')}>
          Shop list
        </button>
      </div>

      {tab === 'visits' ? <Visits canLog={canLog} /> : <ShopList canLog={canLog} />}
    </div>
  );
}

function Visits({ canLog }: { canLog: boolean }) {
  const visits = useQuery({ queryKey: ['shop-visits'], queryFn: () => api.get<ShopVisit[]>('/api/shop-visits') });
  const [stages, setStages] = useState<string[]>(STAGES.filter((s) => s !== 'picked_up'));
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<ShopVisit | 'new' | null>(null);

  const all = useMemo(() => visits.data ?? [], [visits.data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((v) =>
      (stages.length === 0 || stages.includes(v.stage))
      && (!q || [v.unit, v.vin, v.shopName, v.shopAddress, v.issues, v.decidedBy].filter(Boolean).join(' ').toLowerCase().includes(q)));
  }, [all, stages, search]);

  const { rows: sorted, sort, onSort } = useTableSort(rows, {
    inOn: (v) => v.inOn,
    unit: (v) => v.unit,
    shop: (v) => v.shopName,
    estimate: (v) => v.estimate,
    stage: (v) => STAGES.indexOf(v.stage),
    pickedUp: (v) => v.pickedUpOn,
  });

  const count = (s: ShopStage) => all.filter((v) => v.stage === s).length;
  const open = all.filter((v) => v.stage !== 'picked_up');

  const exportRows = () => downloadCsv(
    `fleet-shop-visits-${today()}.csv`,
    ['Date in', 'Name', 'VIN', 'Shop', 'Shop address', 'Shop phone', 'Shop email', 'Issues', 'Estimate',
      'Stage', 'Approved on', 'Denied on', 'Decided by', 'Picked up', 'Notes'],
    sorted.map((v) => [
      v.inOn, v.unit, v.vin, v.shopName, v.shopAddress, v.shopPhone, v.shopEmail, v.issues, v.estimate,
      STAGE_LABEL[v.stage], v.approvedOn, v.deniedOn, v.decidedBy, v.pickedUpOn, v.notes,
    ]),
  );

  return (
    <>
      {visits.isError && <ErrorBanner message={(visits.error as Error).message} />}

      {visits.data && (
        <div className="stat-grid">
          <div className="stat stat--clickable" onClick={() => setStages(STAGES.filter((s) => s !== 'picked_up'))}>
            <div className="stat__label">AT A SHOP NOW</div>
            <div className="stat__value">{open.length}</div>
            <div className="stat__note">not picked up yet</div>
          </div>
          <div className="stat stat--clickable" onClick={() => setStages(['awaiting_decision'])}>
            <div className="stat__label">ESTIMATE TO APPROVE</div>
            <div className="stat__value" style={{ color: count('awaiting_decision') ? 'var(--orange-text)' : undefined }}>
              {count('awaiting_decision')}
            </div>
            <div className="stat__note">{money(all.filter((v) => v.stage === 'awaiting_decision').reduce((n, v) => n + (v.estimate ?? 0), 0))} waiting</div>
          </div>
          <div className="stat stat--clickable" onClick={() => setStages(['awaiting_estimate'])}>
            <div className="stat__label">WAITING FOR ESTIMATE</div>
            <div className="stat__value">{count('awaiting_estimate')}</div>
            <div className="stat__note">the shop hasn't priced it</div>
          </div>
          <div className="stat stat--clickable" onClick={() => setStages(['approved'])}>
            <div className="stat__label">IN REPAIR</div>
            <div className="stat__value">{count('approved')}</div>
            <div className="stat__note">approved, not picked up</div>
          </div>
        </div>
      )}

      <div className="filter-row">
        <input className="control filter-row__search" placeholder="Find: name, shop, issue…" aria-label="Find a visit"
          value={search} onChange={(e) => setSearch(e.target.value)} />
        <MultiSelect keepOrder options={STAGES.map((s) => ({ value: s, label: STAGE_LABEL[s], count: count(s) }))}
          selected={stages} onChange={setStages} allLabel="Every visit" label="Stage" />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <ExportMenu onExport={exportRows} />
          {canLog && <button className="btn btn--primary" onClick={() => setEditing('new')}>Log a visit</button>}
        </div>
      </div>

      <div className="meta" style={{ marginBottom: 8 }}>
        {visits.isLoading ? 'Loading visits…' : `${sorted.length} of ${all.length} visits`}
      </div>

      <div className="table-wrap only-wide">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="IN" column="inOn" sort={sort} onSort={onSort} />
              <SortableHeader label="NAME" column="unit" sort={sort} onSort={onSort} />
              <SortableHeader label="SHOP" column="shop" sort={sort} onSort={onSort} />
              <th>ISSUES</th>
              <SortableHeader label="ESTIMATE" column="estimate" sort={sort} onSort={onSort} className="num" />
              <SortableHeader label="STAGE" column="stage" sort={sort} onSort={onSort} />
              <SortableHeader label="PICKED UP" column="pickedUp" sort={sort} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {visits.isLoading && <EmptyRow colSpan={7}>Loading visits…</EmptyRow>}
            {visits.data && sorted.length === 0 && (
              <EmptyRow colSpan={7}>{all.length === 0 ? 'No shop visits logged yet.' : 'No visit matches these filters.'}</EmptyRow>
            )}
            {sorted.map((v) => (
              <tr key={v.shopVisitId} className={v.canEdit ? 'clickable' : undefined}
                onClick={() => v.canEdit && setEditing(v)}>
                <td style={{ whiteSpace: 'nowrap' }}>{v.inOn ? formatDate(v.inOn) : '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <Link className="strong" to={`/units/${v.fleetAssetId}`} onClick={(e) => e.stopPropagation()}
                    style={{ textDecoration: 'none' }}>{v.unit}</Link>
                </td>
                <td>
                  <div style={{ color: 'var(--text)' }}>{v.shopName}</div>
                  {v.shopPhone && <div className="meta">{v.shopPhone}</div>}
                </td>
                <td style={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={v.issues ?? undefined}>
                  {v.issues ?? '—'}
                </td>
                <td className="num">
                  {money(v.estimate)}
                  {v.estimateFiles.length > 0 && (
                    <div>
                      <button type="button" className="ms__link" title={v.estimateFiles[0].fileName}
                        onClick={(e) => { e.stopPropagation(); void openEstimate(v.shopVisitId, v.estimateFiles[0]); }}>
                        PDF
                      </button>
                    </div>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <StageText stage={v.stage} />
                  {(v.approvedOn || v.deniedOn) && (
                    <div className="meta">{formatDate((v.approvedOn ?? v.deniedOn)!)}{v.decidedBy ? ` · ${v.decidedBy}` : ''}</div>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{v.pickedUpOn ? formatDate(v.pickedUpOn) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row-cards only-narrow">
        {sorted.map((v) => (
          <div key={v.shopVisitId} className="row-card" onClick={() => v.canEdit && setEditing(v)}>
            <div className="row-card__top">
              <span className="strong">{v.unit}</span>
              <StageText stage={v.stage} />
            </div>
            <div className="row-card__line">{v.shopName}{v.inOn ? ` · in ${formatDate(v.inOn)}` : ''}</div>
            {v.issues && <div className="row-card__line">{v.issues}</div>}
            <div className="row-card__line">{v.estimate != null ? `Estimate ${money(v.estimate)}` : 'No estimate yet'}</div>
          </div>
        ))}
      </div>

      {editing && <VisitForm visit={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function ShopList({ canLog }: { canLog: boolean }) {
  const shops = useQuery({ queryKey: ['shops'], queryFn: () => api.get<Shop[]>('/api/shops') });
  const [showRetired, setShowRetired] = useState(false);
  const [editing, setEditing] = useState<Shop | 'new' | null>(null);

  const all = useMemo(() => shops.data ?? [], [shops.data]);
  const rows = useMemo(() => all.filter((s) => showRetired || s.isActive), [all, showRetired]);
  const { rows: sorted, sort, onSort } = useTableSort(rows, {
    name: (s) => shopLabel(s),
    open: (s) => s.openVisits,
    visits: (s) => s.visits,
  });

  const exportRows = () => downloadCsv(
    `fleet-shops-${today()}.csv`,
    ['Shop', 'Address', 'Phone', 'Email', 'At the shop now', 'Visits', 'Still used', 'Notes'],
    sorted.map((s) => [s.name, s.address, s.phone, s.email, s.openVisits, s.visits, s.isActive ? 'Yes' : 'No', s.notes]),
  );

  return (
    <>
      {shops.isError && <ErrorBanner message={(shops.error as Error).message} />}
      <div className="filter-row">
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)' }}>
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} />
          Include shops no longer used
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <ExportMenu onExport={exportRows} />
          {canLog && <button className="btn btn--primary" onClick={() => setEditing('new')}>Add a shop</button>}
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader label="SHOP" column="name" sort={sort} onSort={onSort} />
              <th>ADDRESS</th>
              <th>PHONE</th>
              <th>EMAIL</th>
              <SortableHeader label="AT THE SHOP NOW" column="open" sort={sort} onSort={onSort} style={{ textAlign: 'center' }} />
              <SortableHeader label="VISITS" column="visits" sort={sort} onSort={onSort} style={{ textAlign: 'center' }} />
            </tr>
          </thead>
          <tbody>
            {shops.isLoading && <EmptyRow colSpan={6}>Loading shops…</EmptyRow>}
            {shops.data && sorted.length === 0 && <EmptyRow colSpan={6}>No shops on the list yet.</EmptyRow>}
            {sorted.map((s) => (
              <tr key={s.shopId} className={canLog ? 'clickable' : undefined} onClick={() => canLog && setEditing(s)}
                style={s.isActive ? undefined : { opacity: 0.55 }}>
                <td className="strong">{s.name}{!s.isActive && <span className="meta"> · no longer used</span>}</td>
                <td style={{ whiteSpace: 'pre-line' }}>{s.address ?? '—'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>{s.phone ? <a href={`tel:${s.phone}`} onClick={(e) => e.stopPropagation()}>{s.phone}</a> : '—'}</td>
                <td>{s.email ? <a href={`mailto:${s.email}`} onClick={(e) => e.stopPropagation()}>{s.email}</a> : '—'}</td>
                <td style={{ textAlign: 'center' }}>{s.openVisits || '—'}</td>
                <td style={{ textAlign: 'center' }}>{s.visits}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <ShopForm shop={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}
